import {InvalidArgumentException} from "../../errors/Exceptions.js";
import {TmTransitionRule} from "./TmTransitionRule.js";
import {TmState} from "./TmState.js";
import {
    containsEqualElement,
    isString, removeRepeatsAndEmptyItems
} from "../../utils.js";


/**
 * Class for Turing machine Transition relation
 *
 * For each <code>(fromState, lookedChar)</code> has array of **unique** possible transitions
 */
export class TmTransitionsRelation{
    #transitionRules = new Map(); // Map: {fromState, lookedChar} -> TmTransitionRule[]
    // (hash by string representation of {fromState, lookedChar}: https://stackoverflow.com/a/31953390)

    #plainTransitionRules;                      // TmTransitionRule[] (without repeats and empty items)
    // contains all transition rules from Map


    /**
     *
     * @param transitionRules {TmTransitionRule[]} (non-empty, but can have empty items)
     * -- all transition rules that transition relation should consist of.
     */
    constructor(transitionRules) {
        // validation
        if(!Array.isArray(transitionRules)){
            throw new InvalidArgumentException("transitionRules must be an Array");
        }
        let elementsCount = 0;
        transitionRules.forEach((transitionRule, i) => {
            if(!(transitionRule instanceof TmTransitionRule)){
                throw new InvalidArgumentException(`each element of transitionRules array must be a TmTransitionRule object (caused by index: ${i})`);
            }
            elementsCount++;
        });
        if(elementsCount === 0){
            throw new InvalidArgumentException("transitionRules array can't be empty");
        }

        // filling this.#transitionRules map
        transitionRules.forEach((transitionRule) => {
            let key = TmTransitionsRelation.#getKey(transitionRule.fromState, transitionRule.lookedChar);
            // for equal rules, keys are also equal

            if(this.#transitionRules.has(key)){
                let existingTransitionRulesForKey = this.#transitionRules.get(key); // array

                if(!containsEqualElement(existingTransitionRulesForKey, transitionRule)){
                    existingTransitionRulesForKey.push(transitionRule);
                    this.#transitionRules.set(key, existingTransitionRulesForKey);
                }
            }
            else{
                this.#transitionRules.set(key, [transitionRule]);
            }
        });

        this.#plainTransitionRules = removeRepeatsAndEmptyItems(transitionRules);

    }

    /**
     * Creates string representation of given <code>fromState</code> and <code>lookedChar</code>,
     * that is used to navigate in <code>Map</code> of transition rules
     *
     * Representation format: <code>{fromState}{lookedChar}</code>.
     * @param fromState {TmState}
     * @param lookedChar {string} 1 character
     * @returns {string} created string representation of given params
     */
    static #getKey(fromState, lookedChar){
        if(!(fromState instanceof TmState)){
            throw new InvalidArgumentException("fromState must be a valid TmState object");
        }
        if(!isString(lookedChar)){
            throw new InvalidArgumentException("lookedChar must be a string");
        }
        if(lookedChar.length !== 1){
            throw new InvalidArgumentException("lookedChar must have length 1");
        }

        return `{${fromState.toShortString()}}{${lookedChar}}`;
    }

    /**
     * Checks whether any transition rule for given <code>fromState</code> and <code>lookedChar</code> exist, or not.
     * @param fromState {TmState}
     * @param lookedChar {string} must contain only 1 character
     * @return {boolean}
     */
    doesTransitionRuleExist(fromState, lookedChar){
        let key = TmTransitionsRelation.#getKey(fromState, lookedChar);
        return this.#transitionRules.has(key);
    }

    /**
     * Gets all possible transition rules for given <code>fromState</code> and <code>lookedChar</code>.
     * @param fromState {TmState}
     * @param lookedChar {string} must contain only 1 character
     * @return {TmTransitionRule[] | undefined} array of transition rules if any exist for given parameters,
     * <code>undefined</code> otherwise.
     */
    getPossibleTransitionRules(fromState, lookedChar){
        let key = TmTransitionsRelation.#getKey(fromState, lookedChar);
        return this.#transitionRules.get(key);
    }

    /**
     * This property can be used to check if this transition relation corresponds to alphabet of Turing machine.
     * @return {string[]} array of unique characters that is used in this TransitionRelation
     */
    get usedChars(){
        let usedChars = [];
        for (const transitionRules of this.#transitionRules.values()) { // transitionRules is TmTransitionRule[]
            transitionRules.forEach((transitionRule) => {
                if(!containsEqualElement(usedChars, transitionRule.lookedChar)){
                    usedChars.push(transitionRule.lookedChar);
                }
                if(!containsEqualElement(usedChars, transitionRule.charToWrite)){
                    usedChars.push(transitionRule.charToWrite);
                }
            });
        }

        return usedChars;
    }

    /**
     * This property can be used to check if this transition relation corresponds to states array of Turing machine.
     * @return {TmState[]} array of unique states that is used in this TransitionRelation
     */
    get usedStates(){
        let usedStates = []; // TmState[]
        for (const transitionRules of this.#transitionRules.values()) { // transitionRules is TmTransitionRule[]
            transitionRules.forEach((transitionRule) => {
                if(!containsEqualElement(usedStates, transitionRule.fromState)){
                    usedStates.push(transitionRule.fromState);
                }
                if(!containsEqualElement(usedStates, transitionRule.toState)){
                    usedStates.push(transitionRule.toState);
                }
            });
        }

        return usedStates;
    }

    /**
     * Checks if only one transition rule exists for each <code>state</code> and <code>lookedChar</code>.
     * @return {boolean}
     */
    isDeterministic(){
        for (const transitionRules of this.#transitionRules.values()) { // transitionRules is TmTransitionRule[]
            // transitionsRules does not contain repeats -- by construction

            if(transitionRules.length > 1){
                return false;
            }
        }

        return true;
    }

    /**
     *
     * @returns {TmTransitionRule[]}
     */
    get plainTransitionRules(){
        return this.#plainTransitionRules;
    }

    /**
     * Compares two <code>TmTransitionsRelation</code> objects
     * (by using <code>equals()</code> method of their <code>plainTransitionRules</code>).
     * @param other {TmTransitionsRelation}
     * @returns {boolean}
     */
    equals(other){
        if(!(other instanceof TmTransitionsRelation)){
            return false;
        }

        if(this === other){
            return true;
        }

        return this.plainTransitionRules.length === other.plainTransitionRules.length &&
            this.plainTransitionRules.every(rule => containsEqualElement(other.plainTransitionRules, rule));
    }

    /**
     *
     * @return {string} string representation of TransitionRelation -- in format of
     * <code>\n</code>-separated list of transition rules.
     */
    toString(){
        let str = "";

        for(const key of this.#transitionRules.keys()){
            this.#transitionRules.get(key)?.forEach((transitionRule) => {
                str += transitionRule.toString() + "\n";
            })
        }
        str = str.slice(0, -1); // delete last \n

        return str;
    }

}